import React, { useState, useEffect, useCallback } from 'react';
import { getStaff } from '../../../_services/api/staffTurnover';
import { getTeamIdFromURL } from '../../../_helpers/methods';
import Pagination from '../Pagination';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
const LIMIT = 20;

const STATUS_COLORS = {
    active: { bg: '#e8f5e9', text: '#2e7d32', dot: '#4caf50' },
    leaving: { bg: '#fff3e0', text: '#e65100', dot: '#ff9800' },
    exited: { bg: '#ffebee', text: '#c62828', dot: '#f44336' },
    finished: { bg: '#f3e5f5', text: '#6a1b9a', dot: '#9c27b0' },
    new: { bg: '#e3f2fd', text: '#1565c0', dot: '#2196f3' },
};

function StatusBadge({ status }) {
    const c = STATUS_COLORS[status] || STATUS_COLORS.leaving;
    return (
        <span
            style={{
                padding: '3px 10px',
                borderRadius: 12,
                background: c.bg,
                color: c.text,
                fontSize: 11,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
            }}
        >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot }} />
            {status}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleDateString();
    } catch {
        return iso;
    }
}

function StaffTable({ team, refreshTrigger = 0, startDate, endDate }) {
    const [rows, setRows] = useState(null); // Changed from [] to null
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [loading, setLoading] = useState(false);

    const loadStaff = useCallback(
        async (p = 1) => {
            setLoading(true);
            setRows(null);
            try {
                setPage(p);
                const res = await getStaff({
                    team: team || getTeamIdFromURL() || undefined,
                    page: p,
                    limit: LIMIT,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                });
                const normalized = (res.results || []).map((emp) => ({
                    ...emp,
                    user: emp?.user || {},
                }));
                setRows(normalized);
                setTotalResults(res.totalResults || 0);
                setPage(p);
            } catch (e) {
                if (e?.name === 'AbortError') {
                    return;
                }
                toast.error(e?.message);
                console.error('Failed to load staff list:', e.message);
            } finally {
                setLoading(false);
            }
        },
        [team, startDate, endDate]
    );

    useEffect(() => {
        loadStaff(1);
    }, [team, startDate, endDate, loadStaff, refreshTrigger]);

    const cols = ['Name', 'Team', 'Status', 'Last Form', 'Last Sub', 'Tenure', 'Days Since', 'SOS+EOS', 'Notes'];

    const renderBody = () => {
        if (loading || rows === null) {
            return (
                <tr>
                    <td colSpan={cols.length} style={{ padding: 24, textAlign: 'center', color: '#999' }}>
                        <VscLoading className="mr-2 spin" />
                        Loading staff...
                    </td>
                </tr>
            );
        }

        if (rows.length === 0) {
            return (
                <tr>
                    <td colSpan={cols.length} style={{ padding: 24, textAlign: 'center', color: '#999' }}>
                        No EVV staff found for the selected team.
                    </td>
                </tr>
            );
        }

        return rows.map((row, idx) => (
            <tr
                key={row.employeeId || idx}
                data-testid={`staff-row-${idx}`}
                style={{
                    borderBottom: '1px solid #f0f0f0',
                    opacity: row.isManager || row.isDuplicate ? 0.55 : 1,
                }}
            >
                <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontWeight: 600 }}>
                        {row.user?.name || '-'}
                        {row.isManager && (
                            <span
                                style={{
                                    fontSize: 10,
                                    color: '#9c27b0',
                                    marginLeft: 6,
                                    background: '#f3e5f5',
                                    padding: '1px 6px',
                                    borderRadius: 8,
                                }}
                            >
                                MGR
                            </span>
                        )}
                        {row.isDuplicate && (
                            <span
                                style={{
                                    fontSize: 10,
                                    color: '#e65100',
                                    marginLeft: 6,
                                    background: '#fff3e0',
                                    padding: '1px 6px',
                                    borderRadius: 8,
                                }}
                            >
                                DUP
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>{row?.user?.email}</div>
                </td>
                <td style={{ padding: '8px 12px' }}>{(row.team || {})?.name || '-'}</td>
                <td style={{ padding: '8px 12px' }}>
                    <StatusBadge status={row.status} />
                </td>
                <td style={{ padding: '8px 12px' }}>{row.lastFormType ? row.lastFormType.toUpperCase() : '-'}</td>
                <td style={{ padding: '8px 12px' }}>{fmtDate(row.lastSubDate)}</td>
                <td style={{ padding: '8px 12px' }}>{row.tenureDays !== null ? `${row.tenureDays}d` : '-'}</td>
                <td style={{ padding: '8px 12px' }}>{row.daysSinceLast !== null ? `${row.daysSinceLast}d` : '-'}</td>
                <td style={{ padding: '8px 12px' }}>{row.totalSosEos || 0}</td>
                <td style={{ padding: '8px 12px', fontSize: 12, color: '#999' }}>
                    {row.leaveReason ||
                        (row.isManager
                            ? 'Manager (excluded from turnover)'
                            : row.isDuplicate
                              ? 'Duplicate account (excluded from turnover)'
                              : '-')}
                </td>
            </tr>
        ));
    };

    return (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
                <table
                    data-testid="staff-turnover-table"
                    style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
                >
                    <thead>
                        <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                            {cols.map((c) => (
                                <th key={c} style={{ padding: '10px 12px', fontWeight: 600, color: '#666' }}>
                                    {c}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{renderBody()}</tbody>
                </table>
            </div>
            {totalResults > LIMIT && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '8px 16px',
                        borderTop: '1px solid #f0f0f0',
                    }}
                >
                    <Pagination
                        totalRecords={totalResults}
                        pageLimit={LIMIT}
                        pageNeighbours={1}
                        pageNo={page}
                        onPageChanged={(paginationData) => loadStaff(paginationData.currentPage)}
                    />
                </div>
            )}
        </div>
    );
}

export default StaffTable;
export { StatusBadge, fmtDate };
